import React, { useState } from 'react';
import { Eye, Download, X, FileText, GitBranch } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { getVraisemblanceColor, getVraisemblanceBgColor } from '../../utils/vraisemblanceCalculator';
import { generateOperationalScenariosDocx } from '../../utils/operationalScenariosDocxGenerator';

/**
 * SimpleScenarioVisualization - Enhanced with Modes Operatoires support
 * Displays scenario with ability to select and visualize different attack path varieties
 */
const SimpleScenarioVisualization = ({
    scenario,
    title = "Visualisation du Scénario Opérationnel",
    onClose,
    isInline = false,
    onSave
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedMode, setSelectedMode] = useState(null); // null = show all phases, number = show specific mode
    const contentRef = React.useRef(null);

    // Export as PNG
    const exportAsPng = () => {
        if (contentRef.current === null) return;

        toPng(contentRef.current, {
            backgroundColor: '#fff',
            width: contentRef.current.offsetWidth,
            height: contentRef.current.offsetHeight,
        })
            .then((dataUrl) => {
                const link = document.createElement('a');
                link.download = `scenario-${new Date().toISOString().split('T')[0]}.png`;
                link.href = dataUrl;
                link.click();
            });
    };

    // Export as PDF
    const exportAsPdf = () => {
        if (contentRef.current === null) return;

        toPng(contentRef.current, {
            backgroundColor: '#fff',
            width: contentRef.current.offsetWidth,
            height: contentRef.current.offsetHeight,
        })
            .then((dataUrl) => {
                const pdf = new jsPDF('landscape');
                const imgWidth = 297;
                const pageHeight = 210;
                const imgHeight = (contentRef.current.offsetHeight * imgWidth) / contentRef.current.offsetWidth;
                let heightLeft = imgHeight;

                let position = 0;

                pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;

                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                }

                pdf.save(`scenario-${new Date().toISOString().split('T')[0]}.pdf`);
            })
            .catch((err) => {
                console.error('Error exporting PDF:', err);
            });
    };

    // Export as DOCX
    const exportAsDocx = async () => {
        try {
            await generateOperationalScenariosDocx(
                {
                    scenarios: [scenario],
                    attackPaths: [],
                    analysisName: scenario.name || title || 'Scénario Opérationnel',
                    organizationName: ''
                },
                {}
            );
        } catch (err) {
            console.error('Error exporting DOCX:', err);
        }
    };

    // Helper function to get phase color
    const getPhaseColor = (phase) => {
        switch (phase) {
            case 'CONNAITRE': return '#3B82F6';
            case 'RENTRER': return '#EF4444';
            case 'TROUVER': return '#F59E0B';
            case 'EXPLOITER': return '#10B981';
            default: return '#6B7280';
        }
    };

    // Helper function to get phase icon
    const getPhaseIcon = (phase) => {
        switch (phase) {
            case 'CONNAITRE': return '1';
            case 'RENTRER': return '2';
            case 'TROUVER': return '3';
            case 'EXPLOITER': return '4';
            default: return '•';
        }
    };

    // Get action details from operational steps - supports both AI and manual ID formats
    const getActionDetails = (actionId) => {
        const allSteps = scenario.operationalSteps || scenario.steps || [];
        return allSteps.find(step =>
            step.elementaryActionId === actionId ||
            step.id === actionId ||
            step._id === actionId
        );
    };

    if (!scenario) {
        return (
            <div className="p-8 text-center text-gray-500">
                Aucun scénario sélectionné pour visualisation
            </div>
        );
    }

    // Determine which steps to show based on selected mode
    let stepsToShow = scenario.operationalSteps || scenario.steps || [];

    if (selectedMode !== null && scenario.modesOperatoires && scenario.modesOperatoires[selectedMode]) {
        const mode = scenario.modesOperatoires[selectedMode];
        // Filter steps to only show those in the selected mode's path
        stepsToShow = mode.path?.map(actionId => getActionDetails(actionId)).filter(Boolean) || stepsToShow;
    }

    return (
        <div className={`scenario-visualization ${isFullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : ''}`}>
            {/* Header */}
            {(!isInline || (isInline && onClose)) && (
                <div className="bg-white border-b border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className={`font-bold text-slate-800 flex items-center ${isInline ? 'text-lg' : 'text-xl'}`}>
                                <Eye size={isInline ? 20 : 24} className="mr-3 text-indigo-600" />
                                {title}
                            </h2>
                            {!isInline && (
                                <p className="text-slate-600 mt-1">
                                    Visualisation du scénario opérationnel
                                </p>
                            )}
                        </div>

                        <div className="flex items-center space-x-2">
                            <button
                                onClick={exportAsPng}
                                className="flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition duration-200 text-sm"
                                title="Exporter en PNG"
                            >
                                <Download size={16} className="mr-1" />
                                PNG
                            </button>

                            <button
                                onClick={exportAsPdf}
                                className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition duration-200 text-sm"
                                title="Exporter en PDF"
                            >
                                <FileText size={16} className="mr-1" />
                                PDF
                            </button>

                            <button
                                onClick={exportAsDocx}
                                className="flex items-center px-3 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition duration-200 text-sm"
                                title="Exporter en Word (.docx)"
                            >
                                <FileText size={16} className="mr-1" />
                                Word
                            </button>

                            {onClose && (
                                <button
                                    onClick={onClose}
                                    className="flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition duration-200 text-sm"
                                    title="Fermer"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div
                ref={contentRef}
                className="p-6 bg-gray-50"
                style={{
                    minHeight: isFullscreen ? 'calc(100vh - 80px)' : (isInline ? '600px' : '600px'),
                    overflowY: 'auto'
                }}
            >
                {/* Scenario Header Card */}
                <div className="mb-8 p-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-blue-200 shadow-lg max-w-7xl mx-auto">
                    <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center mr-4 shadow-md">
                            <Eye className="text-white" size={20} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-blue-900">{scenario.name || scenario.title || 'Scénario Opérationnel'}</h3>
                            <p className="text-sm text-blue-700 opacity-80">Scénario Opérationnel IA</p>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="mb-6 p-4 bg-white bg-opacity-70 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-800">{scenario.description || 'Aucune description disponible'}</p>
                    </div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        {scenario.severity && (
                            <div className="bg-white bg-opacity-70 rounded-lg p-3 text-center border border-red-200">
                                <div className="text-red-600 font-semibold text-xs mb-1">Sévérité</div>
                                <div className="text-red-800 font-bold">{scenario.severity}</div>
                            </div>
                        )}
                        {scenario.likelihood && (
                            <div className="bg-white bg-opacity-70 rounded-lg p-3 text-center border border-orange-200">
                                <div className="text-orange-600 font-semibold text-xs mb-1">Probabilité</div>
                                <div className="text-orange-800 font-bold">{scenario.likelihood}</div>
                            </div>
                        )}
                        {scenario.timeline && (
                            <div className="bg-white bg-opacity-70 rounded-lg p-3 text-center border border-green-200">
                                <div className="text-green-600 font-semibold text-xs mb-1">Durée</div>
                                <div className="text-green-800 font-bold">{scenario.timeline}</div>
                            </div>
                        )}
                    </div>

                    {/* Attack phases preview */}
                    <div className="flex justify-center items-center space-x-2 text-sm">
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-blue-500 mr-1"></div>
                            <span className="text-blue-700 font-medium">CONNAITRE</span>
                        </div>
                        <div className="text-gray-400">→</div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
                            <span className="text-red-700 font-medium">RENTRER</span>
                        </div>
                        <div className="text-gray-400">→</div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-amber-500 mr-1"></div>
                            <span className="text-amber-700 font-medium">TROUVER</span>
                        </div>
                        <div className="text-gray-400">→</div>
                        <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
                            <span className="text-green-700 font-medium">EXPLOITER</span>
                        </div>
                    </div>
                </div>

                {/* Modes Operatoires Selector */}
                {(() => {
                    const retainedModes = (scenario.modesOperatoires || []).filter((m) => m && m.retained !== false);
                    return retainedModes.length > 0;
                })() && (
                    <div className="mb-8 max-w-7xl mx-auto">
                        <div className="bg-white rounded-xl border-2 border-indigo-200 shadow-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3">
                                <h4 className="text-white font-bold flex items-center">
                                    <GitBranch className="mr-2" size={20} />
                                    Modes Opératoires ({(() => (scenario.modesOperatoires || []).filter((m) => m && m.retained !== false).length)} variantes d'attaque)
                                </h4>
                                <p className="text-indigo-100 text-sm mt-1">
                                    Sélectionnez un mode pour visualiser son chemin d'attaque spécifique
                                </p>
                            </div>

                            <div className="p-4 bg-indigo-50">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                    {(scenario.modesOperatoires || []).filter((m) => m && m.retained !== false).map((mode, index) => {
                                        const isSelected = selectedMode === index;
                                        const vColor = getVraisemblanceColor(mode.vraisemblance || 2);
                                        const vBgColor = getVraisemblanceBgColor(mode.vraisemblance || 2);

                                        return (
                                            <button
                                                key={mode.id || index}
                                                onClick={() => setSelectedMode(index)}
                                                className={`p-4 rounded-lg border-2 transition-all ${isSelected
                                                    ? 'border-indigo-500 bg-white shadow-lg scale-105'
                                                    : 'border-indigo-200 bg-white hover:border-indigo-400 hover:shadow-md'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-bold text-slate-800">
                                                        Mode {index + 1}
                                                    </span>
                                                    <div
                                                        className="px-2 py-1 rounded text-xs font-bold"
                                                        style={{ backgroundColor: vColor, color: 'white' }}
                                                    >
                                                        V:{mode.vraisemblance || 2}/4
                                                    </div>
                                                </div>
                                                <div className="text-xs text-slate-600 mb-2">
                                                    {mode.path?.length || 0} actions
                                                </div>
                                                {isSelected && (
                                                    <div className="text-xs text-indigo-600 font-semibold">
                                                        ✓ Sélectionné
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Show all modes option */}
                                <button
                                    onClick={() => setSelectedMode(null)}
                                    className={`mt-3 w-full p-3 rounded-lg border-2 transition-all ${selectedMode === null
                                        ? 'border-indigo-500 bg-white shadow-lg'
                                        : 'border-indigo-200 bg-white hover:border-indigo-400'
                                        }`}
                                >
                                    <span className="font-semibold text-slate-800">
                                        {selectedMode === null ? '✓ ' : ''}Afficher toutes les phases (vue standard)
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Steps Timeline - HORIZONTAL LAYOUT */}
                {stepsToShow && stepsToShow.length > 0 ? (
                    <div className="relative w-full">
                        {/* Horizontal timeline line */}
                        <div className="absolute top-8 left-4 right-4 h-1 bg-gray-200"></div>

                        {/* Steps container */}
                        <div className="w-full">
                            <div className="flex gap-4 px-4" style={{ width: '100%' }}>
                                {stepsToShow.map((step, index) => {
                                    const phaseColor = getPhaseColor(step.phase);
                                    const phaseIcon = getPhaseIcon(step.phase);

                                    return (
                                        <div key={index} className="relative flex-1 min-w-0" style={{ flex: `1 1 ${100 / stepsToShow.length}%` }}>
                                            {/* Timeline dot */}
                                            <div
                                                className="absolute left-1/2 transform -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center z-10 shadow-md"
                                                style={{ backgroundColor: phaseColor, top: '8px' }}
                                            >
                                                <span className="text-white font-bold text-sm">{phaseIcon}</span>
                                            </div>

                                            {/* Content card */}
                                            <div
                                                className="mt-12 bg-white p-5 rounded-xl shadow-lg border-t-4"
                                                style={{ borderTopColor: phaseColor }}
                                            >
                                                {/* Phase header */}
                                                <div className="mb-3">
                                                    <h4 className="font-bold text-lg" style={{ color: phaseColor }}>
                                                        {step.phase}
                                                    </h4>
                                                    <p className="text-gray-700 font-medium">{step.technique || step.name || `Étape ${index + 1}`}</p>
                                                </div>

                                                {/* Description */}
                                                <div className="mb-4">
                                                    <p className="text-gray-600 text-sm line-clamp-3">{step.description}</p>
                                                </div>

                                                {/* Metadata */}
                                                <div className="flex flex-wrap gap-2 mb-4">
                                                    {step.duration && (
                                                        <div className="bg-gray-100 px-3 py-1 rounded-full text-xs text-gray-700">
                                                            <strong>Durée:</strong> {step.duration}
                                                        </div>
                                                    )}
                                                    {step.difficulty && (
                                                        <div className="bg-gray-100 px-3 py-1 rounded-full text-xs text-gray-700">
                                                            <strong>Difficulté:</strong> {typeof step.difficulty === 'number' ? `${step.difficulty}/4` : step.difficulty}
                                                        </div>
                                                    )}
                                                    {step.vraisemblance && (
                                                        <div className="bg-gray-100 px-3 py-1 rounded-full text-xs text-gray-700">
                                                            <strong>V:</strong> {step.vraisemblance}/4
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Tools */}
                                                {step.tools && step.tools.length > 0 && (
                                                    <div>
                                                        <h5 className="font-semibold text-gray-800 mb-2 text-sm flex items-center">
                                                            <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: phaseColor }}></div>
                                                            Outils:
                                                        </h5>
                                                        <div className="flex flex-wrap gap-1">
                                                            {step.tools.slice(0, 3).map((tool, idx) => (
                                                                <span key={idx} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium border border-green-200">
                                                                    {tool.name || tool}
                                                                </span>
                                                            ))}
                                                            {step.tools.length > 3 && (
                                                                <span className="text-gray-500 italic text-xs px-2 py-1">
                                                                    +{step.tools.length - 3} outils
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Arrow to next step */}
                                            {index < stepsToShow.length - 1 && (
                                                <div className="absolute top-10 -right-4 z-20">
                                                    <div className="w-8 h-8 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center shadow-md">
                                                        <span className="text-gray-500 text-sm">→</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center p-8 bg-white rounded-lg shadow border border-gray-200">
                        <p className="text-gray-500">Ce scénario ne contient pas d'étapes détaillées.</p>
                    </div>
                )}
            </div>

            {/* Footer */}
            {!isInline && (
                <div className="p-4 border-t border-slate-200 text-sm text-slate-500 bg-slate-50">
                    <p>
                        <strong>Visualisation :</strong> Explorez les étapes du scénario •
                        <strong>Export :</strong> Sauvegardez la visualisation en PNG ou PDF
                        {scenario.modesOperatoires && scenario.modesOperatoires.length > 0 && (
                            <> • <strong>Modes :</strong> Sélectionnez différentes variantes d'attaque</>
                        )}
                    </p>
                </div>
            )}
        </div>
    );
};

export default SimpleScenarioVisualization;